import { z } from "zod";

/**
 * Jobs and the timeclock (`HR_TIMECLOCK`).
 *
 * The substrate three things above it need: tip pooling, tip-out, and
 * occupation-coded tip reporting. Each needs exactly one of the two answers
 * these tables hold — *which occupation* and *for how long*.
 */

export const TIP_STATUSES = ["directly_tipped", "indirectly_tipped", "untipped"] as const;
export type TipStatus = (typeof TIP_STATUSES)[number];

export const JobSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  tipStatus: z.enum(TIP_STATUSES),
  /** Treasury Tip Occupation Code, for W-2 Box 14b. Required from TY2026. */
  occupationCode: z.string().nullable(),
  sort: z.number().int(),
  status: z.enum(["enabled", "disabled"]),
});
export type Job = z.infer<typeof JobSchema>;

export const SaveJobSchema = z.object({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(80),
  tipStatus: z.enum(TIP_STATUSES).default("untipped"),
  occupationCode: z.string().max(16).nullable().default(null),
  sort: z.number().int().min(0).default(0),
  status: z.enum(["enabled", "disabled"]).default("enabled"),
});

/**
 * Written out rather than `SaveJobSchema.partial()`.
 *
 * `.partial()` makes every field optional but **keeps its `.default()`**, so a
 * PATCH that only renamed a job would arrive at the handler carrying
 * `tipStatus: "untipped"` and quietly take a server out of the tip pool. That
 * is the exact bug `patch-schemas.test.ts` exists for, and it caught this one.
 */
export const PatchJobSchema = z.object({
  code: z.string().min(1).max(32).optional(),
  name: z.string().min(1).max(80).optional(),
  tipStatus: z.enum(TIP_STATUSES).optional(),
  occupationCode: z.string().max(16).nullable().optional(),
  sort: z.number().int().min(0).optional(),
  status: z.enum(["enabled", "disabled"]).optional(),
});

export const TimeEntrySchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  employeeName: z.string(),
  jobId: z.uuid(),
  jobName: z.string(),
  tipStatus: z.enum(TIP_STATUSES),
  clockedInAt: z.string(),
  clockedOutAt: z.string().nullable(),
  businessDate: z.string(),
  /** Whole minutes on the clock. Null while the entry is still open. */
  minutes: z.number().int().nullable(),
  editedByName: z.string().nullable(),
  editedReason: z.string().nullable(),
});
export type TimeEntry = z.infer<typeof TimeEntrySchema>;

/** A manager correcting what a timesheet says. Never a self-service action. */
export const CorrectTimeEntrySchema = z.object({
  clockedInAt: z.string().optional(),
  clockedOutAt: z.string().nullable().optional(),
  reason: z.string().min(1).max(240),
});

// ── Tip pooling and tip-out ─────────────────────────────────────────────────

export const POOL_BASES = ["hours", "points"] as const;
export type PoolBasis = (typeof POOL_BASES)[number];

export const TipPoolSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  basis: z.enum(POOL_BASES),
  isActive: z.boolean(),
  jobs: z.array(z.object({ jobId: z.uuid(), jobName: z.string(), points: z.number().int() })),
});
export type TipPool = z.infer<typeof TipPoolSchema>;

export const SaveTipPoolSchema = z.object({
  name: z.string().min(1).max(80),
  basis: z.enum(POOL_BASES).default("hours"),
  isActive: z.boolean().default(true),
});

/** Written out rather than `.partial()`, for the reason `PatchJobSchema` gives. */
export const PatchTipPoolSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  basis: z.enum(POOL_BASES).optional(),
  isActive: z.boolean().optional(),
});

/** The membership is replaced whole, the same shape a recipe is edited in. */
export const SetTipPoolJobsSchema = z.object({
  jobs: z
    .array(z.object({ jobId: z.uuid(), points: z.number().int().min(0).max(1000).default(1) }))
    .max(50),
});

export const RunTipPoolSchema = z.object({
  businessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
});

export const TipShareSchema = z.object({
  employeeId: z.uuid(),
  employeeName: z.string(),
  jobId: z.uuid(),
  jobName: z.string(),
  minutes: z.number().int(),
  weight: z.string(),
  amount: z.string(),
});

export const TipDistributionSchema = z.object({
  id: z.uuid(),
  poolId: z.uuid(),
  poolName: z.string(),
  businessDate: z.string(),
  totalAmount: z.string(),
  currency: z.string(),
  status: z.enum(["draft", "final"]),
  finalisedAt: z.string().nullable(),
  shares: z.array(TipShareSchema),
});
export type TipDistribution = z.infer<typeof TipDistributionSchema>;

// ── An employee's own hours and tips ────────────────────────────────────────

/** The same YYYY-MM-DD shape `RunTipPoolSchema` validates a business date by. */
const BUSINESS_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * No `employeeId`. The session it is asked over is the scope — see the doc
 * comment on `apps/api/src/routes/employee.ts` — and a field naming somebody
 * else would only invite a client to try it.
 */
export const MyHoursQuerySchema = z
  .object({
    from: z.string().regex(BUSINESS_DATE_REGEX, "YYYY-MM-DD"),
    to: z.string().regex(BUSINESS_DATE_REGEX, "YYYY-MM-DD"),
  })
  .refine((query) => query.from <= query.to, {
    message: "from must not be after to.",
    path: ["to"],
  })
  .refine(
    (query) => {
      const days =
        (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000;
      return days <= 93;
    },
    { message: "The span is at most 93 days.", path: ["to"] },
  );

export const MyTimeEntrySchema = z.object({
  id: z.uuid(),
  jobName: z.string(),
  clockedInAt: z.string(),
  clockedOutAt: z.string().nullable(),
  businessDate: z.string(),
  /** Whole minutes on the clock. Null while the entry is still open. */
  minutes: z.number().int().nullable(),
  /** True when a manager corrected this entry rather than it being lived. */
  edited: z.boolean(),
});
export type MyTimeEntry = z.infer<typeof MyTimeEntrySchema>;

export const MyHoursResponseSchema = z.object({
  data: z.array(MyTimeEntrySchema),
  /** Closed entries only — an open one has nothing yet to add. */
  totalMinutes: z.number().int(),
});

export const MyTipShareSchema = z.object({
  distributionId: z.uuid(),
  businessDate: z.string(),
  poolName: z.string(),
  jobName: z.string(),
  minutes: z.number().int(),
  amount: z.string(),
  currency: z.string(),
});
export type MyTipShare = z.infer<typeof MyTipShareSchema>;

export const MyTipsResponseSchema = z.object({
  data: z.array(MyTipShareSchema),
  /** One total per currency — a store's currency can change, so these never add. */
  totals: z.array(z.object({ currency: z.string(), amount: z.string() })),
});
